import { useCallback, useMemo } from 'react'
import { useIntl, FormattedMessage } from 'react-intl'
import styled from 'styled-components'
import { rem } from 'polished'
import moment from 'moment'

// LuxKit Components
import BodyText from 'LuxKit/Typography/BodyText'
import Group from 'components/utils/Group'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import CardCarousel from 'LuxKit/Carousel/CardCarousel'
import Caption from 'LuxKit/Typography/Caption'
import FormatCurrency from 'components/Common/FormatCurrency'
import Places from 'constants/places'
import Clickable from 'components/Common/Clickable'
import focusStyle from 'styles/utils/focusStyle'
import { mediaHoverable } from 'lib/theme/mediaQueries'
import clsx from 'clsx'
import { useAppSelector } from 'hooks/reduxHooks'
import getHasLoyaltyAccount from 'loyalty/selectors/getHasLoyaltyAccount'
import { getOfferListKeyForUser } from 'lib/offer/offerListKey'
import { buildBundleOfferFilters } from '../MaldivesDirectUtils'
import { HOTEL_OFFERS_LIMIT, MALDIVES_BUNDLE_DEFAULT_NIGHTS } from 'constants/charterFlight'
import { sumUpOccupancies } from 'lib/offer/occupancyUtils'
import { isRewardFareClass, toBaseFareClass, type FlightsClassTypes, type FareClassWithReward } from 'constants/flight'
import type { CalendarItemBundleData, DateItem, DateRange } from '../hooks/types'
import type { MaldivesCabinHotelEntry } from '../hooks/useMaldivesOffersPricing'
import config from 'constants/config'
import { LUXURY_ESCAPES } from 'constants/brands'
import { getLowestAvailableHotelPrice, useSeatEligiblePassengers } from '../hooks/utils'
import { OptimizelyFeatureFlags } from 'constants/featureFlags'
import { useRewardSeatsEnabled } from 'hooks/Flights/useRewardSeatsEnabled'
import { CharterRewardAvailabilityContext } from '../hooks/charterRewardAvailabilityContext'
import { useMaldivesCharterCabinJourneys } from '../hooks/useMaldivesCharterCabinJourneys'
import { expandMaldivesCharterFareClassesWithRewards, getCharterSelectableCashCabins } from 'lib/maldivesCharter/charterCabins'
import { useMaldivesCharterMonthRewardState } from '../hooks/useMaldivesCharterMonthRewardState'
import { useEligibleDateRangesByMonth } from '../hooks/useEligibleDateRangesByMonth'
import { dateHasInfiniteMemberPrice } from './maldivesCharterTripOptionsTablePricing'
import ClassicRewardsSealIcon from 'LuxKit/Icons/loyalty/ClassicRewardsSealIcon'
import SolidFireIcon from 'LuxKit/Icons/solid/SolidFireIcon'
import FormatLoyaltyPoints from 'loyalty/components/Formatters/FormatLoyaltyPoints'
import MaldivesCharterTripOptionsTable from './MaldivesCharterTripOptionsTable'
import MaldivesCharterInfiniteMemberPriceToggle from 'components/OfferPage/Filters/CharterFlight/MaldivesCharterInfiniteMemberPriceToggle'
import getLoyaltyIsInfiniteAccountTier from 'loyalty/selectors/getLoyaltyIsInfiniteAccountTier'
import { trimWhere } from 'lib/array/arrayUtils'
import TextLink from 'LuxKit/TextLink'
import { buildSearchParamsForFilterPane } from 'components/Pages/DestinationLandingPage/DestinationLandingPageUtils'
import { INTL_SHORT_MONTH } from 'constants/dateFormats'
import { intlDateFormatter } from 'lib/datetime/dateUtils'
import useFeatureFlag from 'hooks/Experiments/useFeatureFlag'
import HotelPriceToggle from 'components/Hotels/HotelPriceToggle'
import { FARE_COL_MIN_WIDTH, STICKY_DATE_COL_WIDTH } from './maldivesCharterTripOptionsTableStyles'
import { getMaldivesDesktopCalendarPriceContextLabel } from './maldivesMobileCalendarPriceContext'

// Types

interface TripOption {
  id: string
  departDate: string
  returnDate: string
  checkIn: string
  checkOut: string
  price: number
  memberPrice?: number
  currency: string
  seatsLeft?: number
  nights: number
  available: boolean
  month?: string
  fareClass?: FareClassWithReward
}

const ScrollableContainer = styled(VerticalSpacer)`
  width: 100%;
  min-width: 0;
`

// Styled Components - Improved Month Selector (inspired by TourV2)
const MonthTile = styled(Clickable)`
  position: relative;
  padding: ${rem(8)} ${rem(12)};
  text-align: center;
  border: 1px solid ${props => props.theme.palette.neutral.surface.lightForeground};
  min-height: ${rem(110)};
  min-width: ${rem(104)};
  background-color: ${props => props.theme.palette.neutral.surface.normalBackground};
  transition: border-color 0.2s, background-color 0.2s;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: ${rem(2)};

  &.hasIconRow {
    justify-content: flex-start;
  }

  &:focus-visible {
    ${focusStyle}
  }

  ${mediaHoverable} {
    &:hover {
      background-color: ${props => props.theme.palette.neutral.surface.lightBackground};
    }
  }

  &.selected {
    border: 1px solid ${props => props.theme.palette.neutral.surface.darkForeground};
  }
`

const MonthTileRewardFooter = styled(Group)`
  margin-top: auto;
  padding-top: ${rem(6)};
  border-top: 1px solid ${props => props.theme.palette.neutral.surface.lightForeground};
  width: 100%;
`

const StyledCardCarousel = styled(CardCarousel)`
  margin-top: ${rem(16)};
`

const CalendarPriceControls = styled(Group)`
  width: 100%;
  margin-top: ${rem(16)};
  margin-inline: auto;
`

const TripOptionsContainer = styled(Group)`
  width: 100%;
  margin-top: ${rem(20)};
`

const CalendarContainer = styled(VerticalSpacer)`
  background-color: ${props => props.theme.palette.neutral.surface.lightBackground};
  padding: ${rem(20)} ${rem(20)} ${rem(8)};
  margin: 0 ${rem(-24)} ${rem(-24)};
`

const BottomLinkContainer = styled.div`
  padding: ${rem(20)};
  display: flex;
  justify-content: center;
`

interface Props {
  onDateSelect: (option: TripOption) => void
  selectedOptionId?: string
  nights?: number
  occupancies?: Array<App.Occupants>
  fareClass: FlightsClassTypes
  /** Full selected fare class including reward variant — used for the "Selected" indicator in the trip table. */
  selectedFareClass?: FareClassWithReward
  priceMode: 'total' | 'perPerson'
  // LuxPlus related
  luxPlusEnabled?: boolean
  dateRangesByMonth: Record<string, Array<DateRange>>
  selectedMonth: string
  onMonthChange: (month: string) => void
  /** Ready-to-use calendar item data - no calculation needed **/
  itemBundleDataByDate?: Record<string, CalendarItemBundleData>
  /** Per-cabin lowest hotel by date so each per-cabin cell in the trip table can use
   *  the hotel min for offers that allow that cabin (independent of page selection). */
  hotelPriceByCabinAndDate?: Partial<Record<FlightsClassTypes, Record<string, MaldivesCabinHotelEntry>>>
  mode: 'search' | 'offer'
  /** When mode is 'offer', pass the offer to show Kids Fly Free label only when offer has campaign tag */
  offer?: App.HotelOffer
  /** When true, hide dates and trip-table rows that don't have reward-seat availability for every leg. */
  rewardSeatsOnly?: boolean
  /** When true, only show dates/rows carrying an Infinite Member (Friends & Family) fare. */
  infiniteMemberOnly?: boolean
  /** When provided (and the member is Infinite tier), renders the "Only show Infinite Member Price" checkbox in the price-mode row. */
  onInfiniteMemberOnlyChange?: (enabled: boolean) => void
  feederOriginAirportCode?: string
  onPriceModeChange?: (priceMode: 'total' | 'perPerson') => void
  /**
   * Optional cabin filter from the modal's class dropdown. When set, the trip-options table
   * narrows visible columns to that base cabin's cash + reward variants.
   */
  cabinFilter?: FlightsClassTypes | null
}

function MaldivesDirectSearchFlightCalendar(props: Props) {
  const {
    onDateSelect,
    selectedOptionId,
    nights = MALDIVES_BUNDLE_DEFAULT_NIGHTS,
    occupancies,
    fareClass,
    priceMode,
    luxPlusEnabled: luxPlusEnabledProp,
    dateRangesByMonth,
    selectedMonth,
    onMonthChange,
    mode,
    itemBundleDataByDate,
    hotelPriceByCabinAndDate,
    rewardSeatsOnly = false,
    infiniteMemberOnly = false,
    onInfiniteMemberOnlyChange,
    feederOriginAirportCode,
    onPriceModeChange,
  } = props

  const intl = useIntl()

  const luxPlusEnabled = Boolean(luxPlusEnabledProp && config.BRAND === LUXURY_ESCAPES)

  const allOfferLists = useAppSelector(state => state.offer.offerLists)
  const allOffers = useAppSelector(state => state.offer.offers)
  const allOfferMetaData = useAppSelector(state => state.offer.searchResultMetadata.offerMetaData)

  // Calculate total passengers for bundle pricing
  const totalPassengers = useMemo(
    () => sumUpOccupancies(occupancies),
    [occupancies],
  )
  const seatEligiblePassengers = useSeatEligiblePassengers(occupancies ?? [{ adults: 2, children: 0 }])

  const effectiveOccupancies = occupancies ?? [{ adults: 2, children: 0 }]
  const isLuxRowEnabled = useFeatureFlag(OptimizelyFeatureFlags.luxRow)
  const selectableCashCabins = useMemo(
    () => getCharterSelectableCashCabins(isLuxRowEnabled),
    [isLuxRowEnabled],
  )

  const { journeysByCabinDate, rewardIndicator, charterRewardContextValue } = useMaldivesCharterCabinJourneys({
    occupancies: effectiveOccupancies,
    dateRangesByMonth,
    fareClass,
    seatEligiblePassengers,
    feederOriginAirportCode,
  })
  const isRewardSeatsEnabled = useRewardSeatsEnabled()
  const memberId = useAppSelector(state => state.auth.account.memberId)
  const hasLoyaltyAccount = useAppSelector(getHasLoyaltyAccount)
  const isInfiniteTier = useAppSelector(getLoyaltyIsInfiniteAccountTier)

  // When the Infinite Member Price filter is on, keep only dates carrying an Infinite/FnF
  // fare so the month carousel drops months that have none. The trip-options table applies
  // the same predicate per row (and shows its own empty state) via the infiniteMemberOnly prop.
  const isDateInfiniteEligible = useCallback(
    (checkIn: string) => dateHasInfiniteMemberPrice({
      checkIn,
      cashCabins: selectableCashCabins,
      getJourneysForCabinDate: charterRewardContextValue.getJourneysForCabinDate,
      hotelPriceByCabinAndDate,
      seatEligiblePassengers,
      getLegResult: charterRewardContextValue.getLegResult,
    }),
    [selectableCashCabins, charterRewardContextValue, hotelPriceByCabinAndDate, seatEligiblePassengers],
  )

  const eligibleDateRangesByMonth = useEligibleDateRangesByMonth({
    infiniteMemberOnly,
    dateRangesByMonth,
    itemBundleDataByDate,
    isDateInfiniteEligible,
  })

  // The cabin used for month-tile flight pricing: cabinFilter (when a cabin dropdown is
  // selected) falls back to the page-level fareClass.
  const cabinFilter = props.cabinFilter ?? null
  const monthTileFareClass = cabinFilter ?? fareClass
  const rewardLockedForLogin = rewardSeatsOnly && (!memberId || !hasLoyaltyAccount)

  const { rewardPreviewByMonth, monthRewardSeals, monthRewardPricing } = useMaldivesCharterMonthRewardState({
    dateRangesByMonth: eligibleDateRangesByMonth,
    journeysByCabinDate,
    rewardIndicator,
    cabinFilter,
    hotelPriceByCabinAndDate,
    itemBundleDataByDate,
    luxPlusEnabled,
    seatEligiblePassengers,
    rewardSeatsOnly,
    rewardLockedForLogin,
    cashCabins: selectableCashCabins,
  })

  // Generate months for the selector with bundle pricing
  const months = useMemo(() => {
    const monthEntries: Array<{
      name: string
      price: number
      /** Lowest reward points for the month (total across all passengers). */
      rewardPoints?: number
      /** Lowest reward cash co-pay for the month (total). */
      rewardCash?: number
      fetching: boolean
      monthLabel: string
      year: number
      unavailable: boolean
      hasPromotion: boolean
    }> = []

    Object.entries(eligibleDateRangesByMonth).forEach(([monthName, dateRanges]) => {
      // Skip months with no date entries
      if (dateRanges.length === 0) return

      // Calculate lowest bundle price per person for this month
      let lowestPrice = 0
      let allResolved = true
      let hasPromotion = false
      if (rewardSeatsOnly && !rewardLockedForLogin && rewardIndicator.isLoading) {
        allResolved = false
      }

      dateRanges.forEach(({ checkIn, checkOut }) => {
        const hasPromotionForDate = selectableCashCabins.some((cabin) => {
          const entry = journeysByCabinDate.get(cabin)?.get(checkIn)
          return !!(entry?.departing?.promotionPrice || entry?.returning?.promotionPrice)
        })
        hasPromotion = hasPromotion || hasPromotionForDate

        // When a cabinFilter is active in search mode, compute per-cabin pricing from
        // journeysByCabinDate + hotelPriceByCabinAndDate. In offer mode, itemBundleDataByDate
        // already reflects the correct pricing so always prefer it when available.
        if (itemBundleDataByDate && (mode === 'offer' || !cabinFilter)) {
          const itemData = itemBundleDataByDate[checkIn]

          // Consider unresolved if item is still fetching
          if (itemData?.fetching) {
            allResolved = false
          }

          // Calculate bundle price based on priceMode and luxPlusEnabled
          const bundlePrice = priceMode === 'total'
            ? (luxPlusEnabled ? (itemData?.memberTotalPrice || itemData?.totalPrice) : itemData?.totalPrice)
            : (luxPlusEnabled ? (itemData?.memberPricePerPerson || itemData?.pricePerPerson) : itemData?.pricePerPerson)

          if (itemData?.available && bundlePrice && bundlePrice > 0) {
            if (lowestPrice === 0 || bundlePrice < lowestPrice) {
              lowestPrice = bundlePrice
            }
          }
        } else if (mode === 'search' || cabinFilter) {
          // Per-cabin pricing: use the cabin's flight journeys + per-cabin hotel price
          const cabinEntry = journeysByCabinDate.get(monthTileFareClass)?.get(checkIn)
          const depFare = cabinEntry?.departing?.price?.all?.totalFare ?? 0
          const retFare = cabinEntry?.returning?.price?.all?.totalFare ?? 0
          const feederEstimatedTotal = (cabinEntry?.departing?.feederEstimatedPrice ?? 0)
            + (cabinEntry?.returning?.feederEstimatedPrice ?? 0)
          const totalFlightFare = depFare + retFare + feederEstimatedTotal

          // Hotel price — prefer per-cabin data when available
          const cabinHotel = hotelPriceByCabinAndDate?.[monthTileFareClass]?.[checkIn]
          let hotelPrice = 0
          if (cabinHotel) {
            hotelPrice = luxPlusEnabled
              ? (cabinHotel.hotelMemberPrice || cabinHotel.hotelPrice || 0)
              : (cabinHotel.hotelPrice || 0)
          } else if (!cabinFilter) {
            // Fallback to Redux offer list for non-filtered search mode
            const hotelFilters: App.OfferListFilters = buildBundleOfferFilters({
              destinationId: Places.Maldives.id,
              destinationName: Places.Maldives.name,
              checkIn,
              checkOut,
              rooms: occupancies || [{ adults: 2, children: 0 }],
              limit: HOTEL_OFFERS_LIMIT,
            })
            const hotelKey = getOfferListKeyForUser(
              { ...hotelFilters, isLuxPlusMember: luxPlusEnabled || undefined },
              luxPlusEnabled,
            )
            const offerList = allOfferLists[hotelKey]
            const offerMetaDataForList = allOfferMetaData[hotelKey]
            const offerIds = offerList?.offerIds || []
            hotelPrice = getLowestAvailableHotelPrice({
              offerIds,
              allOffers,
              offerMetaData: offerMetaDataForList,
              rooms: occupancies || [{ adults: 2, children: 0 }],
              nights,
              luxPlusEnabled,
            })
            const offerListResolved = !!offerList && !offerList.fetching && !!offerMetaDataForList
            if (!offerListResolved) allResolved = false
          }

          const flightResolved = totalFlightFare > 0
          const hotelResolved = hotelPrice > 0
          // When using pre-fetched journeysByCabinDate (cabinFilter path), missing
          // flights mean the date is unavailable, not still loading. Only mark
          // unresolved in the Redux-fallback search path.
          if (!flightResolved && !cabinFilter) allResolved = false

          if (flightResolved && hotelResolved) {
            const bundleTotal = hotelPrice + totalFlightFare
            const bundlePrice = priceMode === 'perPerson' && totalPassengers > 0
              ? bundleTotal / totalPassengers
              : bundleTotal

            if (bundlePrice > 0 && (lowestPrice === 0 || bundlePrice < lowestPrice)) {
              lowestPrice = bundlePrice
            }
          }
        }
      })

      // Extract month and year for display
      const firstDate = dateRanges[0]?.checkIn
      const date = moment(firstDate)

      monthEntries.push({
        name: monthName,
        price: lowestPrice || 0,
        rewardPoints: monthRewardPricing[monthName]?.points,
        rewardCash: monthRewardPricing[monthName]?.cash,
        fetching: !allResolved,
        monthLabel: intlDateFormatter(date.toDate(), INTL_SHORT_MONTH, intl.locale),
        year: date.year(),
        unavailable: allResolved && !lowestPrice,
        hasPromotion,
      })
    })

    return trimWhere(monthEntries, m => m.unavailable)
  }, [intl, eligibleDateRangesByMonth, nights, luxPlusEnabled, totalPassengers, allOfferLists, allOffers, allOfferMetaData, occupancies, priceMode, mode, itemBundleDataByDate, cabinFilter, monthTileFareClass, journeysByCabinDate, hotelPriceByCabinAndDate, rewardSeatsOnly, rewardLockedForLogin, rewardIndicator.isLoading, selectableCashCabins, monthRewardPricing])

  // Generate all date items for ALL months (lightweight - just dates)
  const allDateItems = useMemo(() => {
    const items: Array<DateItem> = []

    // Loop through all months
    Object.entries(dateRangesByMonth).forEach(([monthName, dateRanges]) => {
      // Skip months with no date entries
      if (dateRanges.length === 0) return

      dateRanges.forEach(({ departureDate, returnDate, checkIn, checkOut }) => {
        items.push({
          id: `${checkIn}-${nights}n`,
          checkIn: checkIn,
          checkOut: checkOut,
          departDate: departureDate,
          returnDate: returnDate,
          month: monthName,
        })
      })
    })

    return items
  }, [dateRangesByMonth, nights])

  const handleMonthSelect = useCallback((month: string) => {
    onMonthChange(month)
  }, [onMonthChange])

  const handleOptionSelect = useCallback((option: TripOption) => {
    onDateSelect(option)
  }, [onDateSelect])

  const adultCount = useMemo(
    () => (occupancies ?? [{ adults: 2, children: 0 }]).reduce((count, room) => count + (room.adults ?? 0), 0),
    [occupancies],
  )
  const tripPriceContextLabel = getMaldivesDesktopCalendarPriceContextLabel(intl, nights, priceMode, adultCount)
  const rewardsLabel = intl.formatMessage({
    id: '-TfAGyLuUwWENqOtJZ-E',
    defaultMessage: 'Rewards',
    description: 'Rewards label shown below the divider on Maldives charter month filter cards.',
  })
  const calendarFareClasses = useMemo(() => {
    const fareClasses = isRewardSeatsEnabled
      ? expandMaldivesCharterFareClassesWithRewards(selectableCashCabins)
      : [...selectableCashCabins]

    return fareClasses.filter(fc => {
      if (rewardSeatsOnly && !isRewardFareClass(fc)) return false
      return !props.cabinFilter || toBaseFareClass(fc) === props.cabinFilter
    })
  }, [isRewardSeatsEnabled, rewardSeatsOnly, props.cabinFilter, selectableCashCabins])
  const fareClassColumnCount = calendarFareClasses.length
  const calendarControlsWidth = fareClassColumnCount > 3
    ? rem(1440)
    : `max(calc(${STICKY_DATE_COL_WIDTH} + ${fareClassColumnCount} * ${FARE_COL_MIN_WIDTH}), ${rem(684)})`

  const hasMonthTileIconRow = months.some(month => !month.fetching && (
    month.hasPromotion || (isRewardSeatsEnabled && ((monthRewardSeals[month.name]?.showRewardSeal ?? false) || !hasLoyaltyAccount))
  ))

  // Render month carousel content (reusable)
  const monthCarousel = (
    <StyledCardCarousel
      gap={12}
      alignment="center"
      snap="start"
      gutterSize={0}
      gutterStyle="overflow"
    >
      {months.map((month) => {
        const priceColour = luxPlusEnabled ? 'lux-plus' : undefined
        const showRewardSeal = monthRewardSeals[month.name]?.showRewardSeal ?? false
        const showPromotionIcon = month.hasPromotion && !month.fetching
        const showRewardPrice = rewardSeatsOnly && month.rewardPoints != null && month.rewardCash != null
        return (
          <MonthTile
            key={month.name}
            onClick={() => handleMonthSelect(month.name)}
            className={clsx({
              selected: selectedMonth === month.name,
              hasIconRow: hasMonthTileIconRow,
            })}
          >
            <BodyText variant="small" colour="light-foreground-alt">
              {month.year}
            </BodyText>
            <BodyText variant="large">{month.monthLabel}</BodyText>
            {month.fetching && <Caption variant="small">
              {intl.formatMessage({
                id: 'ahqwZJxNJlNqe4lst3xR',
                defaultMessage: 'Loading...',
                description: 'Loading state text for charter flight month price in the flights section',
              })}
            </Caption>}
            {!month.fetching && showRewardPrice && <>
              <Caption variant="medium" weight="bold" colour={priceColour}>
                <FormatLoyaltyPoints points={month.rewardPoints!} abbreviated />
              </Caption>
              <Caption variant="small" colour={priceColour ?? 'neutral-two'}>
                <FormattedMessage
                  id="nJ9gIfPenFxdQlMiiP0K"
                  defaultMessage="+ {cash}"
                  description="Cash co-pay add-on shown below the points amount on a month tile when Société Rewards is active"
                  values={{ cash: <FormatCurrency value={month.rewardCash!} format="roundedDollar" /> }}
                />
              </Caption>
            </>}
            {!month.fetching && rewardLockedForLogin && rewardPreviewByMonth[month.name] && <>
              <Caption variant="medium" weight="bold" colour={priceColour}>
                <FormatLoyaltyPoints points={rewardPreviewByMonth[month.name].points} abbreviated />
              </Caption>
              <Caption variant="small" colour={priceColour ?? 'neutral-two'}>
                <FormattedMessage
                  id="nJ9gIfPenFxdQlMiiP0K"
                  defaultMessage="+ {cash}"
                  description="Cash co-pay add-on shown below the points amount on a month tile when Société Rewards is active"
                  values={{ cash: <FormatCurrency value={rewardPreviewByMonth[month.name].cash} format="roundedDollar" /> }}
                />
              </Caption>
            </>}
            {!month.fetching && rewardSeatsOnly && !rewardLockedForLogin && !showRewardPrice && <Caption variant="small" colour="neutral-three">
              {intl.formatMessage({
                id: 'LZPm-sfDQqoDODKnM-7E',
                defaultMessage: 'Sold out!',
                description: 'Text shown on month tile when Société Rewards is on but no reward seats exist for that month',
              })}
            </Caption>}
            {!month.fetching && !rewardSeatsOnly && <BodyText variant="large" weight="bold" colour={priceColour}>
              {month.price > 0 && <FormatCurrency value={month.price} format="roundedDollar" />}
              {!month.price && <Caption variant="small">
                {intl.formatMessage({
                  id: 'OlOfqYtNbU_4z3O0cdFc',
                  defaultMessage: 'Unavailable',
                  description: 'Text shown when no charter flights available for month',
                })}
              </Caption>}
            </BodyText>}
            {isRewardSeatsEnabled && (showRewardSeal || !hasLoyaltyAccount) && !month.fetching && <MonthTileRewardFooter direction="horizontal" horizontalAlign="center" verticalAlign="center" gap={4}>
              <ClassicRewardsSealIcon size={20} colour="highlight-primary-dark" aria-hidden />
              <Caption variant="small" weight="bold" colour="highlight-primary-dark">
                {rewardsLabel}
              </Caption>
            </MonthTileRewardFooter>}
            <Group direction="horizontal" horizontalAlign="center" gap={2}>
              {showPromotionIcon && !month.fetching && <SolidFireIcon size="S" colour="urgency" aria-hidden />}
            </Group>
          </MonthTile>
        )
      })}
    </StyledCardCarousel>
  )

  return (
    <CharterRewardAvailabilityContext.Provider value={charterRewardContextValue}>
      <CalendarContainer gap={4}>
        {/* Month Selector - Improved Carousel */}
        <ScrollableContainer gap={0}>
          <div>
            {monthCarousel}
            <CalendarPriceControls direction="horizontal" verticalAlign="center" horizontalAlign="start" gap={8} style={{ maxWidth: calendarControlsWidth }}>
              {onPriceModeChange && <HotelPriceToggle value={priceMode} onChange={onPriceModeChange} dropdownPlacement="bottom-start" dropdownPortaled />}
              <BodyText variant="medium" colour="neutral-two">
                {tripPriceContextLabel}
              </BodyText>
              {isInfiniteTier && onInfiniteMemberOnlyChange && (
                <MaldivesCharterInfiniteMemberPriceToggle
                  checked={infiniteMemberOnly}
                  onChange={onInfiniteMemberOnlyChange}
                />
              )}
            </CalendarPriceControls>
          </div>

          <TripOptionsContainer direction="vertical" gap={0}>
            <MaldivesCharterTripOptionsTable
              currentMonthTripOptions={allDateItems}
              selectedMonth={selectedMonth}
              dateRangesByMonth={dateRangesByMonth}
              luxPlusEnabled={luxPlusEnabled}
              selectedOptionId={selectedOptionId}
              onOptionSelect={handleOptionSelect}
              nights={nights}
              rooms={occupancies || [{ adults: 2, children: 0 }]}
              fareClass={props.selectedFareClass ?? fareClass}
              priceMode={priceMode}
              itemBundleDataByDate={itemBundleDataByDate}
              hotelPriceByCabinAndDate={hotelPriceByCabinAndDate}
              rewardSeatsOnly={rewardSeatsOnly}
              infiniteMemberOnly={infiniteMemberOnly}
              feederOriginAirportCode={feederOriginAirportCode}
              cabinFilter={props.cabinFilter}
            />
            <BottomLinkContainer>
              <BodyText variant="medium" colour="light-foreground-alt">
                <FormattedMessage
                  id="h0D72ZYbNrwG1DVf4SJL"
                  defaultMessage="<strong>Looking for specific dates?</strong> <link>Search all Maldives offers</link>"
                  description="Link at bottom of charter flight calendar to search all Maldives offers"
                  values={{
                    strong: (chunks) => <strong>{chunks}</strong>,
                    link: (chunks) => (
                      <TextLink
                        weight="regular"
                        target="_blank"
                        to={`/search?${buildSearchParamsForFilterPane({
                        originalSearch: '',
                        destinationName: Places.Maldives.name,
                        destinationId: Places.Maldives.id,
                        adults: 2,
                        children: 'none',
                      })}`}
                      >
                        {chunks}
                      </TextLink>
                    ),
                  }}
                />
              </BodyText>
            </BottomLinkContainer>
          </TripOptionsContainer>
        </ScrollableContainer>
      </CalendarContainer>
    </CharterRewardAvailabilityContext.Provider>
  )
}

export default MaldivesDirectSearchFlightCalendar
